import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { AriaFeedbackCategory } from "@alm/validation";
import { PageHeader } from "@/components/app/shell/page-header";
import { guardRoute } from "@/lib/policy/route-guard";
import { optionalInt, optionalOneOf, optionalString, KEEP_PLACE } from "@/lib/search";
import { PlatformAriaFeedbackCard } from "./-components/platform-aria-feedback-card";

type FeedbackSearch = {
  rating?: "up" | "all";
  category?: AriaFeedbackCategory;
  offset?: number;
};

export const Route = createFileRoute("/_authenticated/platform_/aria-feedback")({
  validateSearch: (search: Record<string, unknown>): FeedbackSearch => ({
    rating: optionalOneOf(["up", "all"] as const, search.rating),
    category: (optionalString(search.category)?.slice(0, 40) || undefined) as
      | AriaFeedbackCategory
      | undefined,
    offset: optionalInt(search.offset, 1, 1_000_000),
  }),
  beforeLoad: ({ context }) => guardRoute(context.me, "platform-aria-feedback"),
  component: PlatformAriaFeedback,
});

/** What people said about Aria's answers, for the team that tunes her. */
function PlatformAriaFeedback() {
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<FeedbackSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const rating = params.rating === "all" ? "" : (params.rating ?? "down");
  return (
    <div className="space-y-6">
      <PageHeader
        title="Aria feedback"
        meta="Every rating of an Aria answer, with what was asked and what she said."
      />
      <PlatformAriaFeedbackCard
        rating={rating}
        category={params.category ?? ""}
        offset={params.offset ?? 0}
        onFilter={(next) =>
          patch({
            ...(next.rating !== undefined
              ? { rating: next.rating === "" ? "all" : next.rating === "up" ? "up" : undefined }
              : {}),
            ...(next.category !== undefined ? { category: next.category || undefined } : {}),
            offset: undefined,
          })
        }
        onOffset={(next) => patch({ offset: next > 0 ? next : undefined })}
      />
    </div>
  );
}
